import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/Skeleton";
import { cn } from "@/lib/utils";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const STATUS_TONE = {
    completed: "border-emerald-200 bg-emerald-50 text-emerald-700",
    "in-progress": "border-amber-200 bg-amber-50 text-amber-700",
    "not-started": "border-border bg-secondary text-muted-foreground",
};

const STATUS_LABEL = {
    completed: "Completed",
    "in-progress": "In progress",
    "not-started": "Not started",
};

export default function LearnModulePage() {
    const { module_slug } = useParams();
    const navigate = useNavigate();
    const [moduleMeta, setModuleMeta] = useState(null);
    const [chapters, setChapters] = useState(null);
    const [progress, setProgress] = useState(null);
    const [notFound, setNotFound] = useState(false);
    useDocumentTitle(moduleMeta ? `Learn · ${moduleMeta.name}` : "Learn");

    useEffect(() => {
        let cancelled = false;
        setChapters(null);
        setProgress(null);
        setModuleMeta(null);
        setNotFound(false);

        Promise.all([
            api.get("/learn/modules"),
            api.get("/learn/chapters", { params: { module: module_slug } }),
            api.get("/learn/progress"),
        ])
            .then(([modulesRes, chaptersRes, progressRes]) => {
                if (cancelled) return;
                const meta = modulesRes.data.find((m) => m.slug === module_slug);
                if (!meta) {
                    setNotFound(true);
                    return;
                }
                setModuleMeta(meta);
                setChapters(chaptersRes.data);
                setProgress(progressRes.data);
            })
            .catch(() => {
                if (cancelled) return;
                toast.error("Could not load module");
                setChapters([]);
            });
        return () => { cancelled = true; };
    }, [module_slug]);

    const progressBySlug = React.useMemo(() => {
        const m = {};
        (progress?.by_chapter || []).forEach((p) => { m[p.chapter_slug] = p; });
        return m;
    }, [progress]);

    if (notFound) {
        return (
            <Layout>
                <div className="mx-auto max-w-2xl py-16 text-center" data-testid="learn-module-not-found">
                    <h1 className="text-2xl font-semibold text-foreground">Method not found</h1>
                    <p className="mt-2 text-sm text-muted-foreground">
                        That method isn&apos;t in the library yet.
                    </p>
                    <Button onClick={() => navigate("/learn")} variant="secondary" className="mt-6">
                        Back to library
                    </Button>
                </div>
            </Layout>
        );
    }

    const pct = moduleMeta && moduleMeta.chapter_count
        ? Math.round((moduleMeta.chapters_completed / moduleMeta.chapter_count) * 100)
        : 0;

    return (
        <Layout>
            <div className="space-y-8 animate-fade-in-up" data-testid="learn-module-page">
                <div>
                    <Button asChild variant="ghost" size="sm" className="-ml-2 text-muted-foreground" data-testid="learn-module-back-link">
                        <Link to="/learn">
                            <ArrowLeft className="h-4 w-4" />
                            All methods
                        </Link>
                    </Button>
                </div>

                <header className="space-y-2">
                    {!moduleMeta ? (
                        <>
                            <Skeleton className="h-3 w-20" />
                            <Skeleton className="h-10 w-2/3" />
                            <Skeleton className="h-4 w-1/2" />
                        </>
                    ) : (
                        <>
                            <p className="text-xs font-semibold uppercase tracking-wider text-primary" data-testid="learn-module-eyebrow">
                                Method
                            </p>
                            <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl" data-testid="learn-module-name">
                                {moduleMeta.name}
                            </h1>
                            <p className="max-w-2xl text-base text-muted-foreground" data-testid="learn-module-subtitle">
                                {moduleMeta.subtitle}
                            </p>
                        </>
                    )}
                </header>

                {moduleMeta && (
                    <Card className="border-border/80 shadow-sm" data-testid="learn-module-progress-strip">
                        <CardContent className="space-y-3 p-5">
                            <div className="flex flex-wrap items-baseline justify-between gap-2">
                                <p className="text-sm text-foreground">
                                    <span className="text-xl font-semibold tabular-nums" data-testid="learn-module-progress-completed">
                                        {moduleMeta.chapters_completed}
                                    </span>{" "}
                                    <span className="text-muted-foreground">
                                        of {moduleMeta.chapter_count} chapters complete
                                    </span>
                                    {moduleMeta.user_average_score != null && (
                                        <span className="text-muted-foreground">
                                            {"  ·  Avg quiz score "}
                                            <span className="text-foreground font-medium tabular-nums" data-testid="learn-module-progress-avg">
                                                {Math.round(moduleMeta.user_average_score)}%
                                            </span>
                                        </span>
                                    )}
                                </p>
                            </div>
                            <div
                                className="h-2 w-full overflow-hidden rounded-full bg-secondary"
                                role="progressbar"
                                aria-valuenow={moduleMeta.chapters_completed}
                                aria-valuemin={0}
                                aria-valuemax={moduleMeta.chapter_count}
                                data-testid="learn-module-progress-bar"
                            >
                                <div
                                    className="h-full rounded-full bg-primary transition-all duration-700"
                                    style={{ width: `${pct}%` }}
                                />
                            </div>
                        </CardContent>
                    </Card>
                )}

                {!chapters ? (
                    <div className="space-y-3">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <Skeleton key={i} className="h-24 w-full rounded-2xl" />
                        ))}
                    </div>
                ) : chapters.length === 0 ? (
                    <Card className="border-border/80 shadow-sm">
                        <CardContent className="p-6 text-sm text-muted-foreground">
                            No chapters published for this method yet.
                        </CardContent>
                    </Card>
                ) : (
                    <ul className="space-y-3" data-testid="learn-module-chapter-list">
                        {chapters.map((ch) => {
                            const p = progressBySlug[ch.slug];
                            const status = p?.passed
                                ? "completed"
                                : p?.attempts > 0
                                    ? "in-progress"
                                    : "not-started";
                            return (
                                <li key={ch.id}>
                                    <Link
                                        to={`/learn/chapter/${ch.slug}`}
                                        data-testid={`learn-chapter-link-${ch.slug}`}
                                        className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-2xl"
                                    >
                                        <Card className="group border-border/80 shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md">
                                            <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:gap-5">
                                                <div
                                                    className={cn(
                                                        "flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-sm font-semibold tabular-nums",
                                                        status === "completed"
                                                            ? "border-primary/50 bg-primary text-primary-foreground"
                                                            : "border-border bg-secondary text-foreground"
                                                    )}
                                                    aria-hidden
                                                >
                                                    {status === "completed" ? (
                                                        <CheckCircle2 className="h-5 w-5" />
                                                    ) : (
                                                        ch.order
                                                    )}
                                                </div>
                                                <div className="min-w-0 flex-1 space-y-1.5">
                                                    <h3 className="text-lg font-semibold text-foreground" data-testid={`learn-chapter-title-${ch.slug}`}>
                                                        {ch.title}
                                                    </h3>
                                                    <p className="text-sm text-muted-foreground">{ch.subtitle}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        ≈ {ch.estimated_minutes} min read
                                                        <span className="mx-1.5 text-border">·</span>
                                                        {ch.quiz_question_count}-question quiz
                                                    </p>
                                                </div>
                                                <div className="flex shrink-0 items-center gap-2">
                                                    <span
                                                        className={cn(
                                                            "rounded-full border px-2.5 py-1 text-xs font-medium",
                                                            STATUS_TONE[status]
                                                        )}
                                                        data-testid={`learn-chapter-status-${ch.slug}`}
                                                    >
                                                        {STATUS_LABEL[status]}
                                                        {status === "completed" && p?.best_percentage != null && (
                                                            <span className="ml-1.5 tabular-nums">· best {p.best_percentage}%</span>
                                                        )}
                                                    </span>
                                                    <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </Layout>
    );
}
